import { useQuery } from '@apollo/client';
import { useI18n } from '@i18n';
import { Container, PaperProps } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { PropsWithChildren, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import { PageHeader } from '@/components/organisms/PageHeader/PageHeader';
import Page from '@/components/Page';
import { useSession } from '@/components/session/useSession';
import { GET_PORTFOLIOS } from '@/graphql/Portfolio.queries';
import { PATHS } from '@/routes';
import PortfolioCreateDialog from '@/sections/Org/Portfolio/PortfolioCreateDialog';

interface Props extends PaperProps {
  iconString?: string;
  title: string;
  subTitle?: string;
  onClick?: () => void;
}

export default function Unavailable({
  sx,
  iconString = ICONS.ANT_DESIGN_STOP_OUTLINED,
  title,
  subTitle,
  onClick,
}: Props) {
  const theme = useTheme();
  const RootStyle = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    padding: theme.spacing(3),
    cursor: onClick ? 'pointer' : 'default',
  });

  const handleClick = () => {
    onClick?.();
  };

  return (
    <RootStyle sx={sx} onClick={handleClick}>
      {iconString ? (
        <Iconify icon={iconString} width={50} height={50} sx={{ mb: 1 }} color={theme.palette.grey[500]} />
      ) : null}
      <PTypography variant="h4" color="disabled">
        {title}
      </PTypography>
      {subTitle ? (
        <PTypography variant="body1" align="center" color="disabled">
          {subTitle}
        </PTypography>
      ) : null}
    </RootStyle>
  );
}

type UnavailableForMissingPortfoliosProps = {
  title: string;
  heading: string;
};

export function UnavailablePageForMissingPortfolios({
  title,
  heading,
  children,
}: PropsWithChildren<UnavailableForMissingPortfoliosProps>) {
  const { data } = useQuery(GET_PORTFOLIOS);

  if (data?.portfolio.length === 0) {
    return (
      <Page title={title}>
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <PageHeader>
            <PageHeader.Title>{heading}</PageHeader.Title>
          </PageHeader>
          <UnavailablePortfolioMessage />
        </Container>
      </Page>
    );
  } else {
    return <>{children}</>;
  }
}

function UnavailablePortfolioMessage({ subTitle }: { subTitle?: string }) {
  const { t } = useI18n();
  const { isAdmin } = useSession();
  const [showCreatePortfolioDialog, setShowCreatePortfolioDialog] = useState<boolean>(false);
  const onCloseModal = () => {
    setShowCreatePortfolioDialog(false);
  };

  const unavailableForUser = (
    <Unavailable
      iconString=""
      title={t('UnavailablePortfolio_Title')}
      subTitle={subTitle ?? t('UnavailablePortfolio_SubTitle_User')}
    />
  );
  const unavailableForAdmin = (
    <Unavailable
      iconString={ICONS.GRIDICONS_CREATE}
      title={t('UnavailablePortfolio_Title')}
      subTitle={subTitle ?? t('UnavailablePortfolio_SubTitle')}
      onClick={() => {
        setShowCreatePortfolioDialog(true);
      }}
    />
  );

  return (
    <>
      {isAdmin ? unavailableForAdmin : unavailableForUser}
      <PortfolioCreateDialog open={showCreatePortfolioDialog} onClose={onCloseModal} portfolios={[]} />
    </>
  );
}

export function UnavailablePageForEconomicUnits({ subTitle }: { subTitle?: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const {
    org: { orgSector },
  } = useSession();

  return (
    <Page title={subTitle ?? t('UnavailableForEconomicUnits_SubTitle', { orgSector })}>
      <Container maxWidth="lg" sx={{ mb: 4, mt: 2 }}>
        <Unavailable
          iconString={ICONS.FLUENT_SETTINGS_32_REGULAR}
          title={t('UnavailableForEconomicUnits_Title', { orgSector })}
          subTitle={subTitle ?? t('UnavailableForEconomicUnits_SubTitle', { orgSector })}
          onClick={() => navigate(PATHS.org.settings())}
        />
      </Container>
    </Page>
  );
}
